import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Grid, Card, Icon, Chip, Modal, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useAuth } from "../../../AuthContext";
import { COLORS } from "theme/tokens";

function BankPitfallPage() {
  const { token } = useAuth();
  const [pitfalls, setPitfalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPitfall, setSelectedPitfall] = useState(null);

  const fetchPitfalls = useCallback(async () => {
    try {
      const res = await axios.get("/api/admin/bank-factory/pitfalls", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPitfalls(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("獲取失敗教訓失敗", err);
      setPitfalls([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDelete = useCallback(async (id, reason) => {
    if (!window.confirm(`確定要刪除教訓「${reason?.slice(0, 30)}...」嗎？`)) return;
    try {
      await axios.delete(`/api/admin/bank-factory/pitfalls/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPitfalls();
    } catch (err) {
      alert("刪除失敗：" + (err?.response?.data?.error || err.message));
    }
  }, [token, fetchPitfalls]);

  useEffect(() => {
    if (token) fetchPitfalls();
  }, [token, fetchPitfalls]);

  const columns = React.useMemo(() => [
    { 
      Header: "失敗原因", 
      accessor: "failureReason",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon sx={{ color: COLORS.error, fontSize: "1.2rem !important" }}>report_problem</Icon>
          <MDTypography variant="button" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
      )
    },
    { 
      Header: "市場環境", 
      accessor: "marketRegime", 
      Cell: ({ value }) => (
        <Chip label={value || "ALL"} size="small" sx={{ bgcolor: COLORS.lighter, color: "#e65100", fontWeight: "bold" }} />
      )
    },
    { 
      Header: "適用標的", 
      accessor: "symbol",
      Cell: ({ value }) => (
        <Chip label={value === "ALL" ? "全域" : value} size="small" variant="outlined" />
      )
    },
    { 
      Header: "近期損益 (Ref)", 
      accessor: "recentReturn",
      Cell: ({ value, row }) => (
        <MDTypography variant="caption" color={value < 0 ? "error" : "success"} fontWeight="bold">
          {value}% (MDD: {row.original.recentMDD}%)
        </MDTypography>
      )
    },
    {
      Header: "操作",
      accessor: "actions",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <MDButton variant="text" color="info" onClick={() => setSelectedPitfall(row.original)}>
            查看解決方案
          </MDButton>
          <MDButton variant="text" color="error" onClick={() => handleDelete(row.original.id, row.original.failureReason)}>
            <Icon>delete</Icon>
          </MDButton>
        </MDBox>
      ),
    },
  ], [handleDelete]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: COLORS.black, borderRadius: "10px 10px 0 0" }}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold" color="white">
                    🚨 Beast AI 避坑指南 (Pitfall Guide)
                  </MDTypography>
                  <MDTypography variant="button" color="white" sx={{ opacity: 0.8 }}>
                    記錄失敗教訓，確保 AI 不在同一個地方跌倒兩次
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" onClick={fetchPitfalls}>
                  <Icon>refresh</Icon>
                </MDButton>
              </MDBox>
              <MDBox pt={1}>
                {loading ? (
                  <MDBox p={5} textAlign="center"><CircularProgress /></MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: pitfalls || [] }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* 詳情彈窗 */}
      <Modal open={!!selectedPitfall} onClose={() => setSelectedPitfall(null)}>
        <MDBox sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 600, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
        }}>
          <MDTypography variant="h5" fontWeight="bold" mb={2} color="error" display="flex" alignItems="center" gap={1}>
            <Icon>gavel</Icon> 避險建議：{selectedPitfall?.failureReason}
          </MDTypography>
          
          <MDBox mb={3} p={2} sx={{ bgcolor: COLORS.lighter, borderRadius: 1, borderLeft: "4px solid #ff9800" }}>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
              💡 AI 避雷邏輯：
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {selectedPitfall?.avoidanceLogic}
            </MDTypography>
          </MDBox>

          <MDBox textAlign="right">
            <MDButton onClick={() => setSelectedPitfall(null)} color="dark">我明白了</MDButton>
          </MDBox>
        </MDBox>
      </Modal>
    </DashboardLayout>
  );
}

export default BankPitfallPage;
